import React, {useCallback, useState} from 'react';

import PermissionsDialog, {
	type PermissionsDialogProps,
} from '../../components/Dialog';
import AttributePermissionsProvider, {
	useAttributePermissionProvider,
} from '../../context';

import {AttributeProvider} from '@netx/core-attribute';

import type {AttributePermission, PermissionMode} from '../../types';
import {splitEventHandler} from '@netx/core-next/utils';

export type PermissionsContainerProps = Omit<
	PermissionsDialogProps,
	| 'ListItemComponentProps'
	| 'attributeTemplate'
	| 'data'
	| 'isItemLoaded'
	| 'loadMoreItems'
	| 'total'
	| 'onSubmit'
	| 'onAdd'
> & {
	templateId: number;
	ListItemComponentProps?: PermissionsDialogProps['ListItemComponentProps'];
};

/** we are not paging items (at this time) */
const isItemLoaded = () => true;

const PermissionsContainer = ({
	onClose,
	open,
	DialogProps,
	...props
}: PermissionsContainerProps) => {
	const {actions, state, getAttributeTemplate} =
		useAttributePermissionProvider();

	const [dialogReady, setDialogReady] = useState(false);

	const handlePermissionItemChange = useCallback(
		(permission: AttributePermission, newPerm: PermissionMode) => {
			actions.updatePermission({
				...permission,
				read: false,
				write: false,
				[newPerm]: true,
			});
		},
		[actions.updatePermission],
	);

	const handlePermissionItemRemove = useCallback(
		(permission: AttributePermission) => {
			actions.deletePermission(permission.groupId);
		},
		[actions.deletePermission],
	);

	const handleClose = useCallback(() => {
		actions.resetPermissions();
		onClose?.();
	}, [onClose]);

	// We are not utilizing `useForm`
	const handleSubmit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			event.stopPropagation();
			return actions.save().then(() => {
				handleClose();
			});
		},
		[actions.save],
	);

	return (
		<PermissionsDialog
			hidePager
			onAdd={actions.addPermission}
			data={state.data}
			attributeTemplate={getAttributeTemplate()}
			dataReady={dialogReady && state.ready}
			isItemLoaded={isItemLoaded}
			loadMoreItems={async () => undefined}
			open={open}
			onClose={handleClose}
			onSubmit={handleSubmit}
			/** show _n_ loading items if data has not been loaded yet */
			total={state.ready ? state.data.length : 5}
			hasChanged={state.unsavedChanges}
			{...DialogProps}
			{...props}
			DialogProps={{
				hasChanged: state.unsavedChanges,
				TransitionProps: {
					...DialogProps?.TransitionProps,
					// TODO: this is a hack of sorts for now - will want to transition over to tanstack virtualizer lib
					// and if this stil lproves to be neccessary we should abstract this logic for all dialogs wit hvirtualized content
					onEntered: splitEventHandler(
						DialogProps?.TransitionProps?.onEntered,
						(node, isAppearing) => {
							setDialogReady(true);
						},
					),
				},
			}}
			ListItemComponentProps={{
				...props.ListItemComponentProps,
				onChange: handlePermissionItemChange,
				onRemove: handlePermissionItemRemove,
			}}
		/>
	);
};

export default function (props: PermissionsContainerProps) {
	return (
		<AttributeProvider>
			<AttributePermissionsProvider templateId={props.templateId}>
				<PermissionsContainer {...props} />
			</AttributePermissionsProvider>
		</AttributeProvider>
	);
}
